import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import './Connectors.css';

interface Identity {
  provider: string;
  email?: string;
  lastSeenAt?: string;
}
interface MeResponse {
  user?: { id: string; email: string; name?: string };
  identities?: Identity[];
}

interface ConnectorDef {
  id: string;
  name: string;
  short: string;
  iconCls: 'datacaffe' | 'esg' | 'election' | 'lumara';
  desc: string;
  authorizeUrl?: string;
  live: boolean;
}

// Direct port of CONNECTORS from web-ui/public/connectors.html — same 3
// products, same descriptions.
const DATACAFFE_URL = 'http://localhost:3000';
const CONNECTORS: ConnectorDef[] = [
  {
    id: 'datacaffe', name: 'DataCaffe', short: 'DC', iconCls: 'datacaffe',
    desc: 'BI assistant — list data connections, run SQL on your warehouse, generate dashboards. Authorize so brew-dc-3.1 can answer with your actual data.',
    authorizeUrl: DATACAFFE_URL + '/authorize-brew.html',
    live: true,
  },
  {
    id: 'esg-caffe', name: 'ESG Caffe', short: 'ES', iconCls: 'esg',
    desc: 'Sustainability reporting (BRSR / GRI / SASB / TCFD). Authorize to ask "what were my Q2 Scope 1 emissions?" and have brew-esg-1.2 return the real number.',
    live: false,
  },
  {
    id: 'electioncaffe', name: 'Election Caffe', short: 'EC', iconCls: 'election',
    desc: 'Campaign analytics — voter rolls, booth coverage, cadre tracking. Authorize so brew-election can return your constituency-scoped data.',
    live: false,
  },
];

/**
 * Connectors — port of legacy web-ui/public/connectors.html.
 *
 * Same DOM (.connector card with icon + body + actions), same data
 * path (GET /api/auth/me returns identities[]). Each connector
 * renders connected / not linked / coming soon based on identity match.
 */
export function Connectors() {
  const [linked, setLinked] = useState<Set<string>>(new Set());
  const [byProvider, setByProvider] = useState<Record<string, Identity>>({});
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function reload(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      const ids = me.identities ?? [];
      setLinked(new Set(ids.map((i) => i.provider)));
      setByProvider(Object.fromEntries(ids.map((i) => [i.provider, i])));
    } catch (e) {
      setError((e as Error).message || 'Could not load identities');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const url = new URL(window.location.href);
    const justLinked = url.searchParams.get('linked');
    if (justLinked) {
      setFlash(`${justLinked} linked successfully. Breww can now access your data on your behalf.`);
      url.searchParams.delete('linked');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    }
    void reload();
  }, []);

  async function unlink(provider: string): Promise<void> {
    if (!confirm(`Unlink ${provider}? Breww won't be able to fetch your data from there until you re-authorize.`)) return;
    try {
      await api.delete(`/api/auth/identities/${provider}`);
      await reload();
    } catch (e) {
      alert(`Unlink failed: ${(e as Error).message}`);
    }
  }

  function authorize(url: string | undefined): void {
    if (!url) return;
    window.location.href = url;
  }

  return (
    <div className="connectors-page">
      <div className="connectors-topbar">
        <h1>BREWW AI · Connectors</h1>
        <a className="back" href="/chat">← Back to chat</a>
      </div>

      <div className="connectors-container">
        {flash && <div className="connectors-flash">{flash}</div>}

        <p className="connectors-intro">
          Connectors link your Breww AI account to other Drighna products so the
          chat can pull <b>your data</b>, not just generic answers. After
          authorizing, the related wrappers (e.g. <code>brew-dc-3.1</code>) can
          call that platform's APIs with your identity.
        </p>

        {error && <div className="connectors-error">{error}</div>}

        {loading && !error && <p style={{ color: 'var(--muted-c)' }}>Loading…</p>}

        {!loading && CONNECTORS.map((c) => {
          const isLinked = linked.has(c.id);
          const idn = byProvider[c.id];
          return (
            <div className="connector" key={c.id}>
              <div className={`icon ${c.iconCls}`}>{c.short}</div>
              <div className="body">
                <div className="name">{c.name}</div>
                <div className="desc">{c.desc}</div>
                {isLinked && idn && (
                  <div className="meta">
                    {idn.email || ''}{idn.lastSeenAt ? ` · last used ${new Date(idn.lastSeenAt).toLocaleString()}` : ''}
                  </div>
                )}
              </div>
              <div className="actions">
                {!c.live ? (
                  <>
                    <span className="status disabled">coming soon</span>
                    <button type="button" className="authorize" disabled>Not available</button>
                  </>
                ) : isLinked ? (
                  <>
                    <span className="status connected">connected</span>
                    <button type="button" className="reauthorize" title={`Refresh your ${c.name} session — use this if your connection expired.`} onClick={() => authorize(c.authorizeUrl)}>Reauthorize</button>
                    <button type="button" className="unlink" onClick={() => void unlink(c.id)}>Unlink</button>
                  </>
                ) : (
                  <>
                    <span className="status unlinked">not linked</span>
                    <button type="button" className="authorize" onClick={() => authorize(c.authorizeUrl)}>Authorize</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
